/**
 * Maps legacy `avataaars` (v2) prop values to `@dicebear/avataaars` option values.
 *
 * The old package used PascalCase named strings and named colors.
 * DiceBear uses camelCase variant names and hex color values.
 *
 * All input values originate from fixed enum strings (see enumAvatar.ts), so
 * no user-supplied content reaches this mapper — no sanitization is required.
 */

// ── Top (hair / head) ────────────────────────────────────────────────────────
// null → hide the top component entirely (topProbability: 0)

const TOP_MAP: Record<string, string | null> = {
  NoHair: null,
  Eyepatch: null, // eyepatch is an accessory in DiceBear; hide top
  Hat: 'hat',
  Hijab: 'hijab',
  Turban: 'turban',
  WinterHat1: 'winterHat1',
  WinterHat2: 'winterHat2',
  WinterHat3: 'winterHat3',
  WinterHat4: 'winterHat4',
  LongHairBigHair: 'bigHair',
  LongHairBob: 'bob',
  LongHairBun: 'bun',
  LongHairCurly: 'curly',
  LongHairCurvy: 'curvy',
  LongHairDreads: 'dreads',
  LongHairFrida: 'frida',
  LongHairFro: 'fro',
  LongHairFroBand: 'froBand',
  LongHairNotTooLong: 'longButNotTooLong',
  LongHairShavedSides: 'shavedSides',
  LongHairMiaWallace: 'miaWallace',
  LongHairStraight: 'straight01',
  LongHairStraight2: 'straight02',
  LongHairStraightStrand: 'straightAndStrand',
  ShortHairDreads01: 'dreads01',
  ShortHairDreads02: 'dreads02',
  ShortHairFrizzle: 'frizzle',
  ShortHairShaggyMullet: 'shaggyMullet',
  ShortHairShortCurly: 'shortCurly',
  ShortHairShortFlat: 'shortFlat',
  ShortHairShortRound: 'shortRound',
  ShortHairShortWaved: 'shortWaved',
  ShortHairSides: 'sides',
  ShortHairTheCaesar: 'theCaesar',
  ShortHairTheCaesarSidePart: 'theCaesarAndSidePart'
}

// ── Hair color (named → hex, no # prefix) ───────────────────────────────────

const HAIR_COLOR_MAP: Record<string, string> = {
  Auburn: 'a55728',
  Black: '2c1b18',
  Blonde: 'b58143',
  BlondeGolden: 'd6b370',
  Brown: '724133',
  BrownDark: '4a312c'
}

// ── Accessories ──────────────────────────────────────────────────────────────
// null → hide accessories (accessoriesProbability: 0)

const ACCESSORIES_MAP: Record<string, string | null> = {
  Blank: null,
  Kurt: 'kurt',
  Prescription01: 'prescription01',
  Prescription02: 'prescription02',
  Round: 'round',
  Sunglasses: 'sunglasses',
  Wayfarers: 'wayfarers'
}

// ── Clothing ──────────────────────────────────────────────────────────────────

const CLOTHING_MAP: Record<string, string> = {
  BlazerShirt: 'blazerAndShirt',
  BlazerSweater: 'blazerAndSweater',
  CollarSweater: 'collarAndSweater',
  GraphicShirt: 'graphicShirt',
  Hoodie: 'hoodie',
  Overall: 'overall',
  ShirtCrewNeck: 'shirtCrewNeck',
  ShirtScoopNeck: 'shirtScoopNeck'
}

// ── Clothing color (named → hex) ─────────────────────────────────────────────

const CLOTHES_COLOR_MAP: Record<string, string> = {
  Black: '262e33',
  Blue01: '65c9ff',
  Blue02: '5199e4',
  Blue03: '25557c',
  Gray01: 'e6e6e6',
  Gray02: '929598',
  Heather: '3c4f5c',
  PastelBlue: 'b1e2ff',
  PastelGreen: 'a7ffc4',
  PastelOrange: 'ffdeb5',
  PastelRed: 'ffafb9',
  PastelYellow: 'ffffb1',
  Pink: 'ff488e',
  Red: 'ff5c5c',
  White: 'ffffff'
}

// ── Eyes ──────────────────────────────────────────────────────────────────────
// Notable exceptions: Close → closed, Dizzy → xDizzy

const EYES_MAP: Record<string, string> = {
  Default: 'default',
  Close: 'closed',
  Cry: 'cry',
  Dizzy: 'xDizzy',
  EyeRoll: 'eyeRoll',
  Happy: 'happy',
  Hearts: 'hearts',
  Side: 'side',
  Squint: 'squint',
  Surprised: 'surprised',
  Wink: 'wink',
  WinkWacky: 'winkWacky'
}

// ── Eyebrows ──────────────────────────────────────────────────────────────────

const EYEBROWS_MAP: Record<string, string> = {
  Angry: 'angry',
  AngryNatural: 'angryNatural',
  Default: 'default',
  DefaultNatural: 'defaultNatural',
  FlatNatural: 'flatNatural',
  RaisedExcited: 'raisedExcited',
  RaisedExcitedNatural: 'raisedExcitedNatural',
  SadConcerned: 'sadConcerned',
  SadConcernedNatural: 'sadConcernedNatural',
  UnibrowNatural: 'unibrowNatural',
  UpDown: 'upDown',
  UpDownNatural: 'upDownNatural'
}

// ── Mouth ─────────────────────────────────────────────────────────────────────

const MOUTH_MAP: Record<string, string> = {
  Concerned: 'concerned',
  Default: 'default',
  Disbelief: 'disbelief',
  Eating: 'eating',
  Grimace: 'grimace',
  Sad: 'sad',
  ScreamOpen: 'screamOpen',
  Serious: 'serious',
  Smile: 'smile',
  Tongue: 'tongue',
  Twinkle: 'twinkle',
  Vomit: 'vomit'
}

// ── Skin color (named → hex) ─────────────────────────────────────────────────

const SKIN_COLOR_MAP: Record<string, string> = {
  Tanned: 'fd9841',
  Yellow: 'f8d25c',
  Pale: 'ffdbb4',
  Light: 'edb98a',
  Brown: 'd08b5b',
  DarkBrown: 'ae5d29',
  Black: '614335'
}

// ── Public interface ─────────────────────────────────────────────────────────

export interface LegacyAvataaarsProps {
  avatarStyle?: string
  topType?: string
  accessoriesType?: string
  hairColor?: string
  facialHairType?: string
  clotheType?: string
  clotheColor?: string
  eyeType?: string
  eyebrowType?: string
  mouthType?: string
  skinColor?: string
}

/**
 * Converts legacy avataaars (v2) props into a DiceBear avataaars options object.
 * Unknown/unmapped values are omitted so DiceBear falls back to its defaults.
 */
export function buildDiceBearOptions(
  props: LegacyAvataaarsProps
): Record<string, unknown> {
  const opts: Record<string, unknown> = {
    style: [props.avatarStyle === 'Transparent' ? 'default' : 'circle'],
    facialHairProbability: 0
  }

  // Top (hair)
  if (props.topType !== undefined) {
    const topValue = TOP_MAP[props.topType]
    if (topValue === null) {
      opts.topProbability = 0
      // Special case: Eyepatch is an accessory in DiceBear, not a top
      if (props.topType === 'Eyepatch') {
        opts.accessories = ['eyepatch']
        opts.accessoriesProbability = 100
      }
    } else if (topValue !== undefined) {
      opts.top = [topValue]
      opts.topProbability = 100
    }
  }

  // Hair color
  const hairHex =
    props.hairColor !== undefined ? HAIR_COLOR_MAP[props.hairColor] : undefined
  if (hairHex !== undefined) {
    opts.hairColor = [hairHex]
  }

  // Accessories (skip if Eyepatch topType already set the accessory)
  if (props.accessoriesType !== undefined && props.topType !== 'Eyepatch') {
    const accessoryValue = ACCESSORIES_MAP[props.accessoriesType]
    if (accessoryValue === null) {
      opts.accessoriesProbability = 0
    } else if (accessoryValue !== undefined) {
      opts.accessories = [accessoryValue]
      opts.accessoriesProbability = 100
    }
  }

  // Clothing
  const clothingValue =
    props.clotheType !== undefined ? CLOTHING_MAP[props.clotheType] : undefined
  if (clothingValue !== undefined) {
    opts.clothing = [clothingValue]
  }

  // Clothes color
  const clothesColorHex =
    props.clotheColor !== undefined
      ? CLOTHES_COLOR_MAP[props.clotheColor]
      : undefined
  if (clothesColorHex !== undefined) {
    opts.clothesColor = [clothesColorHex]
  }

  // Eyes
  const eyeValue =
    props.eyeType !== undefined ? EYES_MAP[props.eyeType] : undefined
  if (eyeValue !== undefined) {
    opts.eyes = [eyeValue]
  }

  // Eyebrows
  const eyebrowValue =
    props.eyebrowType !== undefined
      ? EYEBROWS_MAP[props.eyebrowType]
      : undefined
  if (eyebrowValue !== undefined) {
    opts.eyebrows = [eyebrowValue]
  }

  // Mouth
  const mouthValue =
    props.mouthType !== undefined ? MOUTH_MAP[props.mouthType] : undefined
  if (mouthValue !== undefined) {
    opts.mouth = [mouthValue]
  }

  // Skin color
  const skinHex =
    props.skinColor !== undefined ? SKIN_COLOR_MAP[props.skinColor] : undefined
  if (skinHex !== undefined) {
    opts.skinColor = [skinHex]
  }

  return opts
}
